import React, { useState, MouseEvent } from 'react'
import Menu from '@mui/material/Menu'
import FilterListIcon from '@mui/icons-material/FilterList'
import FilterPopover from '../../Common/Filter/FilterPopover'
import { Badge, Button } from '@mui/material'

// Define the props type
interface TaskFilterProps {
  onFilterChange: (filters: Record<string, string[]>) => void
  pageName: string
  teamMembers?: any[]
}

// Define the filter structure
interface FilterItem {
  id: string
  name: string
  filterSubItem: { id: string; label: string; value: string }[]
}

const TaskFilter: React.FC<TaskFilterProps> = ({ onFilterChange, pageName, teamMembers = [] }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const defaultFilters = {
    Status: [] as string[],
    Importance: [] as string[],
    OperationalManager: [] as string[],
    FunctionalArea: [] as string[],
    Supervisor: [] as string[],
  }

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(defaultFilters)
  const [countOfSelectedFilter, setCountOfSelectedFilter] = useState(0)

  // Helper functions to get unique values from team members
  const getUniqueOperationalManagers = () => {
    const managers = teamMembers
      .filter(member => member.operationalManager && member.operationalManager.workdayId)
      .map(member => ({
        id: member.operationalManager.workdayId,
        label: member.operationalManager.name,
        value: member.operationalManager.workdayId
      }))
    
    // Remove duplicates based on workdayId
    const uniqueManagers = managers.filter((manager, index, self) => 
      index === self.findIndex(m => m.value === manager.value)
    )
    
    return uniqueManagers.sort((a, b) => a.label.localeCompare(b.label))
  }

  const getUniqueFunctionalAreas = () => {
    const areas = teamMembers
      .filter(member => member.functionalArea)
      .map(member => ({
        id: member.functionalArea,
        label: member.functionalArea,
        value: member.functionalArea
      }))
    
    // Remove duplicates
    const uniqueAreas = areas.filter((area, index, self) => 
      index === self.findIndex(a => a.value === area.value)
    )
    
    return uniqueAreas.sort((a, b) => a.label.localeCompare(b.label))
  }

  const getUniqueSupervisors = () => {
    const supervisors = teamMembers
      .filter(member => member.supervisor && member.supervisor.workdayId)
      .map(member => ({
        id: member.supervisor.workdayId,
        label: member.supervisor.name,
        value: member.supervisor.workdayId
      }))
    
    // Remove duplicates based on workdayId
    const uniqueSupervisors = supervisors.filter((supervisor, index, self) => 
      index === self.findIndex(s => s.value === supervisor.value)
    )
    
    return uniqueSupervisors.sort((a, b) => a.label.localeCompare(b.label))
  }

  // Filter items for different pages
  const filterItems: FilterItem[] = [
    pageName === 'viewTaskPage'
      ? {
          id: 'status-all-check-box',
          name: 'Status',
          filterSubItem: [
            { id: 'inProgress-check-box', label: 'In Progress', value: 'inProgress' },
            { id: 'notStarted-check-box', label: 'Not Started', value: 'notStarted' },
            { id: 'completed-check-box', label: 'Completed', value: 'completed' },
          ],
        }
      : null,
    {
      id: 'importance-all-check-box',
      name: 'Importance',
      filterSubItem: [
        { id: 'required-check-box', label: 'Required', value: 'Required' },
        { id: 'optional-check-box', label: 'Optional', value: 'Optional' },
      ],
    },
    // Only include assignment fields for pages other than viewTaskPage and manageTaskPage
    ...(pageName !== 'viewTaskPage' && pageName !== 'manageTaskPage' ? [
      {
        id: 'operational-manager-all-check-box',
        name: 'OperationalManager',
        filterSubItem: getUniqueOperationalManagers(),
      },
      {
        id: 'functional-area-all-check-box',
        name: 'FunctionalArea',
        filterSubItem: getUniqueFunctionalAreas(),
      },
      {
        id: 'supervisor-all-check-box',
        name: 'Supervisor',
        filterSubItem: getUniqueSupervisors(),
      },
    ] : []),
  ].filter(Boolean) as FilterItem[] // Filter out `null` items safely

  // Handle opening of the filter menu
  const handleClick = (task: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(task.currentTarget)
  }

  // Handle closing of the filter menu
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Handle filter change
  const handleFilterChange = (type: string, value: string, checked: boolean) => {
    const updatedFilters = checked
      ? [...selectedFilters[type], value]
      : selectedFilters[type].filter((item) => item !== value)

    const newFilters = {
      ...selectedFilters,
      [type]: updatedFilters,
    }

    setSelectedFilters(newFilters)
    onFilterChange(newFilters)

    const countOfSelectedFilters = Object.values(newFilters).flat().length
    setCountOfSelectedFilter(countOfSelectedFilters)
  }

  // Handle select all change
  const handleSelectAllChange = (type: string, checked: boolean) => {
    const allValues =
      filterItems.find((item) => item.name === type)?.filterSubItem.map((item) => item.value) || []

    const newFilters = {
      ...selectedFilters,
      [type]: checked ? allValues : [],
    }

    setSelectedFilters(newFilters)
    onFilterChange(newFilters)

    const countOfSelectedFilters = Object.values(newFilters).flat().length
    setCountOfSelectedFilter(countOfSelectedFilters)
  }

  // Handle reset of filters
  const handleResetFilters = () => {
    setSelectedFilters(defaultFilters)
    onFilterChange(defaultFilters)
    setCountOfSelectedFilter(0)
  }

  return (
    <>
      <Badge badgeContent={countOfSelectedFilter} color='error'>
        <Button
          id='task-filter-button'
          color='success'
          startIcon={<FilterListIcon />}
          onClick={handleClick}
          size='small'
          aria-haspopup='true'
          aria-label='task-filter-button'
          aria-controls={open ? 'Task-filter-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
        >
          Filter
        </Button>
      </Badge>
      <Menu
        id='task-filter-menu'
        MenuListProps={{
          'aria-labelledby': 'task-filter-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <FilterPopover
          filterItems={filterItems}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSelectAllChange={handleSelectAllChange}
        />
        <Button id='filter-reset-button' onClick={handleResetFilters} fullWidth size='small'>
          CLEAR
        </Button>
      </Menu>
    </>
  )
}

export default TaskFilter
